import { configureStore } from '@reduxjs/toolkit';

import { adminApi } from './queries/admin';
import { aiNutritionApi } from './queries/ai-nutrition';
import { authApi } from './queries/auth';
import { constantsApi } from './queries/constants';
import { exercisesApi } from './queries/exercises';
import { fastingPlansApi } from './queries/fasting-plans';
import { fastingSessionsApi } from './queries/fasting-sessions';
import { helpApi } from './queries/help';
import { ingredientsApi } from './queries/ingredients';
import { invitesApi } from './queries/invites';
import { mealLogsApi } from './queries/mealLogs';
import { notificationsApi } from './queries/notifications';
import { postsApi } from './queries/posts';
import { pricingPlansApi } from './queries/pricing-plans';
import { productsApi } from './queries/products';
import { recipesApi } from './queries/recipes';
import { referralsApi } from './queries/referrals';
import { stripeApi } from './queries/stripe';
import { supplementsApi } from './queries/supplements';
import { trackingsApi } from './queries/trackings';
import { userWorkoutsApi } from './queries/user-workouts';
import { usersApi } from './queries/users';
import { workoutsApi } from './queries/workouts';
import {
  authReducer,
  blogFiltersReducer,
  ingredientsSearchFiltersReducer,
  recipesSearchFiltersReducer,
  workoutSearchFiltersReducer,
} from './slices';

export const createStore = () =>
  configureStore({
    reducer: {
      auth: authReducer,
      [adminApi.reducerPath]: adminApi.reducer,
      [aiNutritionApi.reducerPath]: aiNutritionApi.reducer,
      [authApi.reducerPath]: authApi.reducer,
      [constantsApi.reducerPath]: constantsApi.reducer,
      [exercisesApi.reducerPath]: exercisesApi.reducer,
      [fastingPlansApi.reducerPath]: fastingPlansApi.reducer,
      [fastingSessionsApi.reducerPath]: fastingSessionsApi.reducer,
      [helpApi.reducerPath]: helpApi.reducer,
      [postsApi.reducerPath]: postsApi.reducer,
      [recipesApi.reducerPath]: recipesApi.reducer,
      [ingredientsApi.reducerPath]: ingredientsApi.reducer,
      [invitesApi.reducerPath]: invitesApi.reducer,
      [mealLogsApi.reducerPath]: mealLogsApi.reducer,
      [notificationsApi.reducerPath]: notificationsApi.reducer,
      [trackingsApi.reducerPath]: trackingsApi.reducer,
      [usersApi.reducerPath]: usersApi.reducer,
      [userWorkoutsApi.reducerPath]: userWorkoutsApi.reducer,
      [referralsApi.reducerPath]: referralsApi.reducer,
      [stripeApi.reducerPath]: stripeApi.reducer,
      [supplementsApi.reducerPath]: supplementsApi.reducer,
      [pricingPlansApi.reducerPath]: pricingPlansApi.reducer,
      [workoutsApi.reducerPath]: workoutsApi.reducer,
      [productsApi.reducerPath]: productsApi.reducer,
      blogFilters: blogFiltersReducer,
      ingredientsSearchFilters: ingredientsSearchFiltersReducer,
      recipesSearchFilters: recipesSearchFiltersReducer,
      workoutSearchFilters: workoutSearchFiltersReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .concat(adminApi.middleware)
        .concat(aiNutritionApi.middleware)
        .concat(authApi.middleware)
        .concat(constantsApi.middleware)
        .concat(exercisesApi.middleware)
        .concat(fastingPlansApi.middleware)
        .concat(fastingSessionsApi.middleware)
        .concat(helpApi.middleware)
        .concat(postsApi.middleware)
        .concat(recipesApi.middleware)
        .concat(ingredientsApi.middleware)
        .concat(invitesApi.middleware)
        .concat(mealLogsApi.middleware)
        .concat(notificationsApi.middleware)
        .concat(trackingsApi.middleware)
        .concat(usersApi.middleware)
        .concat(userWorkoutsApi.middleware)
        .concat(referralsApi.middleware)
        .concat(stripeApi.middleware)
        .concat(supplementsApi.middleware)
        .concat(pricingPlansApi.middleware)
        .concat(workoutsApi.middleware)
        .concat(productsApi.middleware),
  });

export const store = configureStore({
  reducer: {
    auth: authReducer,
    [adminApi.reducerPath]: adminApi.reducer,
    [aiNutritionApi.reducerPath]: aiNutritionApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [constantsApi.reducerPath]: constantsApi.reducer,
    [exercisesApi.reducerPath]: exercisesApi.reducer,
    [fastingPlansApi.reducerPath]: fastingPlansApi.reducer,
    [fastingSessionsApi.reducerPath]: fastingSessionsApi.reducer,
    [helpApi.reducerPath]: helpApi.reducer,
    [postsApi.reducerPath]: postsApi.reducer,
    [recipesApi.reducerPath]: recipesApi.reducer,
    [ingredientsApi.reducerPath]: ingredientsApi.reducer,
    [invitesApi.reducerPath]: invitesApi.reducer,
    [mealLogsApi.reducerPath]: mealLogsApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [trackingsApi.reducerPath]: trackingsApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [userWorkoutsApi.reducerPath]: userWorkoutsApi.reducer,
    [referralsApi.reducerPath]: referralsApi.reducer,
    [stripeApi.reducerPath]: stripeApi.reducer,
    [supplementsApi.reducerPath]: supplementsApi.reducer,
    [pricingPlansApi.reducerPath]: pricingPlansApi.reducer,
    [workoutsApi.reducerPath]: workoutsApi.reducer,
    [productsApi.reducerPath]: productsApi.reducer,
    blogFilters: blogFiltersReducer,
    ingredientsSearchFilters: ingredientsSearchFiltersReducer,
    recipesSearchFilters: recipesSearchFiltersReducer,
    workoutSearchFilters: workoutSearchFiltersReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(adminApi.middleware)
      .concat(aiNutritionApi.middleware)
      .concat(authApi.middleware)
      .concat(constantsApi.middleware)
      .concat(exercisesApi.middleware)
      .concat(fastingPlansApi.middleware)
      .concat(fastingSessionsApi.middleware)
      .concat(helpApi.middleware)
      .concat(postsApi.middleware)
      .concat(recipesApi.middleware)
      .concat(ingredientsApi.middleware)
      .concat(invitesApi.middleware)
      .concat(mealLogsApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(trackingsApi.middleware)
      .concat(usersApi.middleware)
      .concat(userWorkoutsApi.middleware)
      .concat(referralsApi.middleware)
      .concat(stripeApi.middleware)
      .concat(supplementsApi.middleware)
      .concat(pricingPlansApi.middleware)
      .concat(workoutsApi.middleware)
      .concat(productsApi.middleware),
  devTools: {
    predicate: (_, { type, error }) => {
      if (error) {
        const { name, message } = error;

        if (
          type.includes('rejected') &&
          name === 'ConditionError' &&
          message === 'Aborted due to condition callback returning false.'
        ) {
          return false;
        }
      }

      return true;
    },
  },
});
